import { QuantityUnit } from './grocyApi.ts';

/**
 * Formats an amount with the appropriate quantity unit (singular or plural)
 * @param amount - The amount to format
 * @param quantityUnit - The quantity unit object
 * @returns Formatted string like "1 Piece" or "2 Pieces"
 */
export const formatQuantityUnit = (amount: number, quantityUnit?: QuantityUnit): string => {
  if (amount <= 0) return '';
  
  if (!quantityUnit) {
    return amount.toString();
  }
  
  // Round to 2 decimal places at most
  const roundedAmount = Math.round(amount * 100) / 100;
  
  // Use plural form if amount is not 1, but fall back to singular if name_plural is null
  const unitName = roundedAmount === 1 || !quantityUnit.name_plural 
    ? quantityUnit.name 
    : quantityUnit.name_plural;
  return `${roundedAmount} ${unitName}`;
};

/**
 * Gets the appropriate unit name (singular or plural) based on amount
 * @param amount - The amount
 * @param quantityUnit - The quantity unit object
 * @returns The unit name (singular or plural)
 */
export const getUnitName = (amount: number, quantityUnit?: QuantityUnit): string => {
  if (!quantityUnit) {
    return 'units';
  }
  
  // Use plural form if amount is not 1, but fall back to singular if name_plural is null
  return amount === 1 || !quantityUnit.name_plural 
    ? quantityUnit.name 
    : quantityUnit.name_plural;
};

/**
 * Formats an amount with unit for display in lists
 * @param amount - The amount to format
 * @param quantityUnit - The quantity unit object
 * @param showBrackets - Whether to wrap in brackets (default: false)
 * @returns Formatted string like "1 Piece" or "[2 Pieces]"
 */
export const formatAmountWithUnit = (
  amount: number, 
  quantityUnit?: QuantityUnit, 
  showBrackets: boolean = false
): string => {
  const formatted = formatQuantityUnit(amount, quantityUnit);
  return showBrackets ? `[${formatted}]` : formatted;
}; 